/**
 * Le chantier d'un devis ou d'une facture, et le libellé « Projet » qui le
 * nomme sur le document A4.
 *
 * <p>Le document imprimait l'identifiant brut du chantier, et le plus souvent
 * un tiret : l'interface range le lien sous {@code chantierId}, la base sous
 * {@code siteId}, et la facture convertie depuis un devis ne recevait ni l'un
 * ni l'autre. Une facture de chantier sortait ainsi avec « Projet — ».
 */

const vide = (v) => v == null || v === '';

/**
 * Retrouve le chantier lié à un document par son lien direct.
 *
 * @param {{chantierId?: *, siteId?: *}} doc devis ou facture
 * @param {Array<object>} chantiers chantiers de l'entreprise
 * @returns {object|null} le chantier, ou {@code null} sans lien connu
 */
export function chantierLie(doc, chantiers) {
  const lien = !vide(doc?.chantierId) ? doc.chantierId : doc?.siteId;
  if (vide(lien)) return null;
  // Un chantier tout juste ouvert porte encore son code (« CH-4F2 ») pour
  // identifiant ; la base lui en donne ensuite un numérique, et garde le code.
  return (chantiers || []).find((c) => String(c.id) === String(lien) || c.code === lien) || null;
}

/**
 * Retrouve le chantier ouvert à l'acceptation d'un devis.
 *
 * <p>Un devis signé ne s'écrit plus : c'est le chantier qui porte le lien,
 * sous {@code devisId}, posé à sa création. Il tient quand la base attribue au
 * chantier son identifiant définitif, puisqu'il désigne le devis, déjà
 * enregistré, et non le chantier.
 *
 * @param {{id?: *}} devis le devis
 * @param {Array<object>} chantiers chantiers de l'entreprise
 * @returns {object|null} le chantier, ou {@code null} si aucun n'en est issu
 */
export function chantierIssuDuDevis(devis, chantiers) {
  if (vide(devis?.id)) return null;
  return (
    (chantiers || []).find((c) => !vide(c.devisId) && String(c.devisId) === String(devis.id)) ||
    null
  );
}

/**
 * Retrouve le chantier d'un devis.
 *
 * <p>D'abord le lien direct : celui du devis, puis celui du chantier ouvert à
 * son acceptation. Les chantiers ouverts avant que ce lien existe n'en portent
 * pas : pour eux seuls, le seul chantier du client est le sien, et s'il en a
 * plusieurs, on ne devine pas.
 *
 * @param {object} devis le devis
 * @param {Array<object>} chantiers chantiers de l'entreprise
 * @returns {object|null} le chantier, ou {@code null} faute de certitude
 */
export function chantierDuDevis(devis, chantiers) {
  if (!devis) return null;
  const direct = chantierLie(devis, chantiers) || chantierIssuDuDevis(devis, chantiers);
  if (direct) return direct;
  if (devis.sect === 'magasin' || devis.bonCommande || vide(devis.clientId)) return null;
  // Un chantier issu d'un autre devis n'est pas celui-ci : l'heuristique ne
  // regarde que les chantiers sans devis d'origine connu.
  const duClient = (chantiers || []).filter(
    (c) => c.clientId === devis.clientId && vide(c.devisId),
  );
  return duClient.length === 1 ? duClient[0] : null;
}

/**
 * Devis d'origine d'une facture.
 *
 * @param {{devisId?: *}} doc la facture
 * @param {Array<object>} devis devis de l'entreprise
 * @returns {object|null} le devis, ou {@code null}
 */
function devisDOrigine(doc, devis) {
  if (vide(doc?.devisId)) return null;
  return (devis || []).find((d) => String(d.id) === String(doc.devisId)) || null;
}

/**
 * Chantier d'un devis ou d'une facture : son lien direct, à défaut celui du
 * devis dont la facture est issue.
 *
 * @param {object} doc devis ou facture ({@code devisId} pour une facture)
 * @param {object} [contexte] données de l'entreprise
 * @param {Array<object>} [contexte.chantiers] chantiers
 * @param {Array<object>} [contexte.devis] devis, pour une facture convertie
 * @returns {object|null} le chantier, ou {@code null}
 */
export function chantierDuDocument(doc, { chantiers = [], devis = [] } = {}) {
  if (!doc) return null;
  return chantierLie(doc, chantiers) || chantierDuDevis(devisDOrigine(doc, devis), chantiers);
}

/**
 * Libellé « Projet » d'un document : le nom du chantier lié, à défaut la
 * description du devis d'origine, ou celle du document lui-même.
 *
 * @param {object} doc devis ou facture ({@code devisId} pour une facture)
 * @param {object} [contexte] données de l'entreprise
 * @param {Array<object>} [contexte.chantiers] chantiers
 * @param {Array<object>} [contexte.devis] devis, pour une facture convertie
 * @returns {string} le libellé, ou une chaîne vide s'il n'y a rien à dire
 */
export function libelleProjet(doc, contexte = {}) {
  if (!doc) return '';
  const chantier = chantierDuDocument(doc, contexte);
  if (chantier?.nom) return chantier.nom;
  return String(devisDOrigine(doc, contexte.devis)?.desc || doc.desc || '').trim();
}
